import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusCircle,
  Search,
  Sparkles,
  CheckCircle,
  XCircle,
  Loader2,
  Edit,
  ExternalLink,
  Power,
  PowerOff,
  Eye,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNING_PATH_WORKFLOW_PERMISSION, LEARNING_PATH_CATALOG_PERMISSION } from '@/constants/constant-permissions';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import { useDebounce } from '@/utils/debounce';
import learningPathWorkflowService from '@/services/learning-path-workflow.service';
import { LearningPathWorkflow, WorkflowMetrics } from '@/types/learning-path-workflow.types';
import ActivationConfirmationModal from '@/components/ui/ActivationConfirmationModal';
import { toast } from 'react-hot-toast';

export default function WorkflowList() {
  usePermissionRouteAccess([
    LEARNING_PATH_WORKFLOW_PERMISSION.ADMIN_LEARNING_PATH_WORKFLOW_READ,
    LEARNING_PATH_WORKFLOW_PERMISSION.ADMIN_LEARNING_PATH_WORKFLOW_WRITE,
  ]);
  const canWrite = useHasPermission([LEARNING_PATH_WORKFLOW_PERMISSION.ADMIN_LEARNING_PATH_WORKFLOW_WRITE]);
  const canGoToLP = useHasPermission([LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_WRITE]);

  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<LearningPathWorkflow | null>(null);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  // Debounce search input
  const debouncedSearch = useDebounce(searchQuery, 500);

  // Fetch workflows
  const { data, isLoading, error } = useQuery({
    queryKey: ['workflows', page, itemsPerPage],
    queryFn: async () => {
      const skip = (page - 1) * itemsPerPage;
      return await learningPathWorkflowService.listWorkflows({
        skip,
        limit: itemsPerPage,
      });
    },
  });

  // Fetch workflow metrics
  const { data: metricsData, isLoading: metricsLoading } = useQuery({
    queryKey: ['workflow-metrics'],
    queryFn: async () => {
      return await learningPathWorkflowService.getMetrics();
    },
  });

  // Activate workflow mutation
  const activateMutation = useMutation({
    mutationFn: (id: string) => learningPathWorkflowService.activateWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-metrics'] });
      toast.success('Workflow activated successfully!');
      setShowActivationModal(false);
      setSelectedWorkflow(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: { error_description?: string }; message?: string } } };
      toast.error(err?.response?.data?.error?.error_description || err?.response?.data?.message || 'Failed to activate workflow');
    },
  });

  // Deactivate workflow mutation
  const deactivateMutation = useMutation({
    mutationFn: (id: string) => learningPathWorkflowService.deactivateWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-metrics'] });
      toast.success('Workflow deactivated successfully!');
      setShowActivationModal(false);
      setSelectedWorkflow(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Failed to deactivate workflow');
    },
  });

  // Filter workflows based on search
  const filteredWorkflows = useMemo(() => {
    if (!data?.data?.items) return [];

    let filtered = data.data.items;

    // Apply search filter
    if (debouncedSearch) {
      filtered = filtered.filter(
        (workflow) =>
          workflow.workflow_name
            .toLowerCase()
            .includes(debouncedSearch.toLowerCase()) ||
          workflow.workflow_desc
            ?.toLowerCase()
            .includes(debouncedSearch.toLowerCase()) ||
          workflow.group_key
            ?.toLowerCase()
            .includes(debouncedSearch.toLowerCase()) ||
          workflow.role_key
            ?.toLowerCase()
            .includes(debouncedSearch.toLowerCase()),
      );
    }

    return filtered;
  }, [data, debouncedSearch]);

  // Get metrics from API
  const metrics: WorkflowMetrics = metricsData?.data || {
    not_started: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    total: 0,
    active_workflow: 0,
    inactive_workflow: 0,
  };

  const totalPages = Math.ceil((data?.data?.total || 0) / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  };

  const handleActivationClick = (workflow: LearningPathWorkflow, activating: boolean) => {
    setSelectedWorkflow(workflow);
    setIsActivating(activating);
    setShowActivationModal(true);
  };

  const handleConfirmActivation = () => {
    if (!selectedWorkflow) return;

    if (isActivating) {
      activateMutation.mutate(selectedWorkflow._id);
    } else {
      deactivateMutation.mutate(selectedWorkflow._id);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
            Completed
          </span>
        );
      case 'processing':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
            Failed
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
            Unknown
          </span>
        );
    }
  };

  const getActivationBadge = (activationStatus: string | undefined) => {
    if (activationStatus === 'active') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
          Active
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
          Inactive
        </span>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section with Gradient Background */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Corporate Assignment Workflow
            </h1>
            <p className="text-teal-100">
              Automated learning path generation from job descriptions
            </p>
          </div>
          <Link
            to={canWrite ? "/admin/learning-paths/workflow/create" : "#"}
            onClick={(e) => !canWrite && e.preventDefault()}
            className={`bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2 transition-colors shadow-md ${
              canWrite ? 'hover:bg-teal-50' : 'opacity-50 cursor-not-allowed'
            }`}
            title={!canWrite ? 'You do not have permission to create workflows' : undefined}
          >
            <PlusCircle className="w-5 h-5" />
            Create New Workflow
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Workflows */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm font-medium mb-1">
                  Total Workflows
                </p>
                <p className="text-3xl font-bold text-white">
                  {metricsLoading ? '-' : metrics.total}
                </p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Active */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm font-medium mb-1">Active</p>
                <p className="text-3xl font-bold text-white">
                  {metricsLoading ? '-' : metrics.active_workflow}
                </p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Processing */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm font-medium mb-1">
                  Processing
                </p>
                <p className="text-3xl font-bold text-white">
                  {metricsLoading ? '-' : metrics.processing}
                </p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Loader2 className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Failed */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm font-medium mb-1">Failed</p>
                <p className="text-3xl font-bold text-white">
                  {metricsLoading ? '-' : metrics.failed}
                </p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <XCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search workflow by name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card p-6">
          <SkeletonLoader type="text" count={5} />
        </div>
      ) : error ? (
        <div className="card p-8 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Error Loading Workflows
          </h3>
          <p className="text-gray-600">
            There was an error loading the workflows. Please try again.
          </p>
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="card p-8 text-center">
          <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Workflows Found
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery
              ? 'No workflows match your search criteria.'
              : 'Get started by creating your first workflow.'}
          </p>
          <Link
            to={canWrite ? "/admin/learning-paths/workflow/create" : "#"}
            onClick={(e) => !canWrite && e.preventDefault()}
            className={`btn btn-primary inline-flex items-center gap-2 ${
              !canWrite ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={!canWrite ? 'You do not have permission to create workflows' : undefined}
          >
            <PlusCircle className="w-4 h-4" />
            Create Your First Workflow
          </Link>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Group
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredWorkflows.map((workflow) => (
                  <tr key={workflow._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/learning-paths/workflow/${workflow._id}`}
                        className="font-medium text-gray-900 hover:text-teal-600"
                      >
                        {workflow.workflow_name}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {workflow.workflow_desc || 'No description provided'}
                      </p>
                    </td>

                    <td className="px-6 py-4">
                      <span className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 rounded">
                        {workflow.group_key}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {workflow.role_key ? (
                        <span className="text-sm font-medium text-purple-600">
                          {workflow.role_key}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(workflow.workflow_status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getActivationBadge(workflow.activation_status)}
                        {workflow.workflow_status === 'completed' && (
                          workflow.activation_status === 'active' ? (
                            <button
                              onClick={() => canWrite && handleActivationClick(workflow, false)}
                              disabled={!canWrite}
                              className={`p-2 text-orange-600 rounded transition-colors ${
                                canWrite ? 'hover:text-orange-700 hover:bg-orange-50' : 'opacity-50 cursor-not-allowed'
                              }`}
                              title={!canWrite ? 'You do not have permission' : 'Deactivate Workflow'}
                            >
                              <PowerOff className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => canWrite && handleActivationClick(workflow, true)}
                              disabled={!canWrite}
                              className={`p-2 text-green-600 rounded transition-colors ${
                                canWrite ? 'hover:text-green-700 hover:bg-green-50' : 'opacity-50 cursor-not-allowed'
                              }`}
                              title={!canWrite ? 'You do not have permission' : 'Activate Workflow'}
                            >
                              <Power className="w-4 h-4" />
                            </button>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Link
                          to={canWrite ? `/admin/learning-paths/workflow/${workflow._id}` : "#"}
                          onClick={(e) => !canWrite && e.preventDefault()}
                          className={`p-2 text-gray-600 rounded transition-colors ${
                            canWrite ? 'hover:text-purple-600 hover:bg-purple-50' : 'opacity-50 cursor-not-allowed'
                          }`}
                          title={!canWrite ? 'You do not have permission' : 'View Workflow Status'}
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={canWrite ? `/admin/learning-paths/workflow/${workflow._id}/edit` : "#"}
                          onClick={(e) => !canWrite && e.preventDefault()}
                          className={`p-2 text-gray-600 rounded transition-colors ${
                            canWrite ? 'hover:text-blue-600 hover:bg-blue-50' : 'opacity-50 cursor-not-allowed'
                          }`}
                          title={!canWrite ? 'You do not have permission' : 'Edit Workflow'}
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        {workflow.learning_path_id && (
                          <Link
                            to={canGoToLP ? `/admin/learning-paths/${workflow.learning_path_id}` : "#"}
                            onClick={(e) => !canGoToLP && e.preventDefault()}
                            className={`p-2 text-gray-600 rounded transition-colors ${
                              canGoToLP ? 'hover:text-green-600 hover:bg-green-50' : 'opacity-50 cursor-not-allowed'
                            }`}
                            title={!canGoToLP ? 'You do not have permission' : 'Go to Learning Path'}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={data?.data?.total || 0}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      )}

      {/* Activation Confirmation Modal */}
      <ActivationConfirmationModal
        isOpen={showActivationModal}
        onClose={() => {
          setShowActivationModal(false);
          setSelectedWorkflow(null);
        }}
        onConfirm={handleConfirmActivation}
        title={isActivating ? 'Activate Workflow' : 'Deactivate Workflow'}
        description={
          isActivating
            ? 'Are you sure you want to activate this workflow? This will automatically assign the learning path to the configured group/role.'
            : 'Are you sure you want to deactivate this workflow? This will set the assignment to inactive and cancel all related tracking records.'
        }
        workflowName={selectedWorkflow?.workflow_name}
        isActivating={isActivating}
        isLoading={activateMutation.isPending || deactivateMutation.isPending}
      />
    </div>
  );
}
